import { useState } from "react";
import type { Receivable } from "@/types";
import { formatCOP, formatDate } from "@/utils/format";
import { receivableBalance, receivablePaid } from "@/pages/admin/cartera/components/CarteraStats";

interface ReceivablesPanelProps {
  receivables: Receivable[];
  onPay: (r: Receivable) => void;
  onDelete: (id: string) => void;
}

const ReceivablesPanel = ({ receivables, onPay, onDelete }: ReceivablesPanelProps) => {
  const [confirmId, setConfirmId] = useState<string | null>(null);

  if (receivables.length === 0) {
    return (
      <div className="glass-panel rounded-lg px-5 py-12 text-center">
        <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
          <i className="ri-file-list-3-line text-2xl" />
        </span>
        <p className="text-sm text-foreground-700 font-semibold">Sin cuentas por cobrar</p>
        <p className="text-xs text-foreground-500 mt-1">
          Registra un fiado a un cliente y controla sus abonos desde aquí.
        </p>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-lg overflow-hidden">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-foreground-500 border-b border-accent-500/15">
              <th className="text-left font-semibold px-4 py-3">Cliente</th>
              <th className="text-left font-semibold px-4 py-3">Producto</th>
              <th className="text-left font-semibold px-4 py-3">Fecha</th>
              <th className="text-right font-semibold px-4 py-3">Valor inicial</th>
              <th className="text-right font-semibold px-4 py-3">Abonado</th>
              <th className="text-right font-semibold px-4 py-3">Saldo</th>
              <th className="text-center font-semibold px-4 py-3">Estado</th>
              <th className="text-center font-semibold px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {receivables.map((r) => {
              const paid = receivablePaid(r);
              const balance = receivableBalance(r);
              const settled = balance <= 0;
              return (
                <tr
                  key={r.id}
                  className="border-b border-background-300/20 hover:bg-background-200/30 transition-colors"
                >
                  <td className="px-4 py-3 font-semibold text-foreground-950">{r.clientName}</td>
                  <td className="px-4 py-3 text-foreground-700">{r.productName}</td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{formatDate(r.date)}</td>
                  <td className="px-4 py-3 text-right text-foreground-700">{formatCOP(r.initialValue)}</td>
                  <td className="px-4 py-3 text-right text-accent-200 font-semibold">{formatCOP(paid)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-foreground-950">{formatCOP(balance)}</td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                        settled
                          ? "bg-accent-500/20 text-accent-200"
                          : "bg-secondary-500/20 text-secondary-100"
                      }`}
                    >
                      {settled ? "Pagado" : "Pendiente"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {confirmId === r.id ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            onDelete(r.id);
                            setConfirmId(null);
                          }}
                          className="px-2.5 py-1 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                        >
                          Eliminar
                        </button>
                        <button
                          onClick={() => setConfirmId(null)}
                          className="px-2 py-1 rounded-md text-foreground-500 hover:text-foreground-800 text-[11px] font-semibold cursor-pointer"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => onPay(r)}
                          disabled={settled}
                          className={`w-8 h-8 flex items-center justify-center rounded-md cursor-pointer ${
                            settled
                              ? "text-foreground-500/40 cursor-not-allowed"
                              : "text-foreground-500 hover:text-accent-300 hover:bg-background-200/60"
                          }`}
                          aria-label="Registrar abono"
                          title={settled ? "Cuenta pagada" : "Registrar abono"}
                        >
                          <i className="ri-hand-coin-line" />
                        </button>
                        <button
                          onClick={() => setConfirmId(r.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer"
                          aria-label="Eliminar cuenta"
                          title="Eliminar cuenta"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Vista móvil en tarjetas */}
      <ul className="md:hidden divide-y divide-background-300/25">
        {receivables.map((r) => {
          const paid = receivablePaid(r);
          const balance = receivableBalance(r);
          const settled = balance <= 0;
          return (
            <li key={r.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-950 break-words">{r.clientName}</p>
                  <p className="text-[11px] text-foreground-600 mt-0.5 break-words">{r.productName}</p>
                  <p className="text-[10px] text-foreground-500 mt-0.5">{formatDate(r.date)}</p>
                </div>
                <span
                  className={`shrink-0 inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold ${
                    settled ? "bg-accent-500/20 text-accent-200" : "bg-secondary-500/20 text-secondary-100"
                  }`}
                >
                  {settled ? "Pagado" : "Pendiente"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
                <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2 py-1.5">
                  <p className="text-foreground-500">Inicial</p>
                  <p className="font-semibold text-foreground-800">{formatCOP(r.initialValue)}</p>
                </div>
                <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2 py-1.5">
                  <p className="text-foreground-500">Abonado</p>
                  <p className="font-semibold text-accent-200">{formatCOP(paid)}</p>
                </div>
                <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2 py-1.5">
                  <p className="text-foreground-500">Saldo</p>
                  <p className="font-semibold text-foreground-950">{formatCOP(balance)}</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-2">
                {confirmId === r.id ? (
                  <>
                    <button
                      onClick={() => {
                        onDelete(r.id);
                        setConfirmId(null);
                      }}
                      className="px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                    >
                      Eliminar
                    </button>
                    <button
                      onClick={() => setConfirmId(null)}
                      className="px-3 py-1.5 rounded-md text-foreground-500 hover:text-foreground-800 text-[11px] font-semibold cursor-pointer"
                    >
                      No
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => onPay(r)}
                      disabled={settled}
                      className={`px-3 py-1.5 rounded-md text-[11px] font-semibold whitespace-nowrap inline-flex items-center gap-1.5 ${
                        settled
                          ? "text-foreground-500/40 cursor-not-allowed"
                          : "border border-accent-500/35 text-accent-200 hover:bg-accent-500/10 cursor-pointer"
                      }`}
                    >
                      <i className="ri-hand-coin-line" /> {settled ? "Pagado" : "Abonar"}
                    </button>
                    <button
                      onClick={() => setConfirmId(r.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer"
                      aria-label="Eliminar cuenta"
                      title="Eliminar cuenta"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ReceivablesPanel;